// Communications — the whole-email archive half of N-094 ("forward and
// file"): an email the classifier could not file as a license, and was
// confident enough about, is stored here in full rather than dropped into
// the Intake review queue (lib/intake.ts).
import { signal } from '@preact/signals';
import { authedGet, authedWrite } from './apiClient';
import { refetchResource } from './store';

export const COMMUNICATIONS_KEY = 'communications';

export interface CommunicationListItem {
  id: number;
  subject: string | null;
  fromAddress: string | null;
  fromName: string | null;
  receivedAt: string | null;
  attachmentCount: number;
  /** For the CURRENT viewer — not a global read/unread flag. */
  unread: boolean;
  /** Owner, 2026-09-27: "put the attached in communications with a must
   * read for all managers and Candace … something we track." A global
   * flag, not per-viewer — every mapped role sees it (unlike readBy). */
  mustRead: boolean;
  mustReadSetAt: string | null;
  mustReadSetBy: string | null;
  /** What's New (095): on My Desk until this person marks it read. */
  whatsNew?: boolean;
  /** Not yet published (096) — the owner is the only one ever sent a draft. */
  draft?: boolean;
  /** Owner took it off everyone's My Desk; still listed on Communications. */
  deskRetired?: boolean;
}

export interface CommunicationsData {
  communications: CommunicationListItem[];
  /** Across EVERY communication, never only the ones in the window. */
  unreadCount: number;
  /** First day of the window (Cursor review #61; owner 2026-11-02: last 30 days by default,
   * plus anything older that is still unread or must-read); null = all. */
  since?: string | null;
  /** How many older ones the window left out — the "Show older" button. */
  olderCount?: number;
}

export interface CommunicationAttachment {
  name: string;
  mimeType: string;
  size: number | null;
  /** Where the file itself lives (a Drive link) when it was filed somewhere; a
   * listed-only attachment has none and renders as text. */
  url?: string | null;
}

export interface CommunicationDetail {
  id: number;
  subject: string | null;
  fromAddress: string | null;
  fromName: string | null;
  receivedAt: string | null;
  /** Sanitized server-side (scripts/tracking stripped) — still rendered
   * inside a bounded, scrollable container rather than trusted blindly. */
  bodyHtml: string | null;
  attachments: CommunicationAttachment[];
  emlDriveUrl: string | null;
  unread: boolean;
  mustRead: boolean;
  mustReadSetAt: string | null;
  mustReadSetBy: string | null;
  whatsNew?: boolean;
  draft?: boolean;
  /** The plain text a What's New is written in. */
  bodyText?: string | null;
}

export interface CommunicationReadReceipt {
  email: string;
  readAt: string;
}

export interface CommunicationDetailData {
  communication: CommunicationDetail;
  /** Non-null ONLY for an OWNER viewer — the server omits it for everyone
   * else rather than the client hiding it, so there is nothing to leak by a
   * missed role check here. */
  readBy: CommunicationReadReceipt[] | null;
}

/** Set by "Show older": every later reload of the list (mark read, must
 * read, Refresh) keeps asking for everything until the page is reloaded. */
export const communicationsShowOlder = signal(false);

export function fetchCommunications(): Promise<CommunicationsData> {
  return authedGet(communicationsShowOlder.value ? '/api/v1/communications?all=1' : '/api/v1/communications');
}

export async function showOlderCommunications(): Promise<void> {
  communicationsShowOlder.value = true;
  await refetchResource(COMMUNICATIONS_KEY, fetchCommunications);
}

export function communicationDetailKey(id: number): string {
  return `communication:${id}`;
}

export function fetchCommunicationDetail(id: number): Promise<CommunicationDetailData> {
  return authedGet(`/api/v1/communications/${id}`);
}

/** Idempotent — safe to call every time the detail view opens. */
export async function markCommunicationRead(id: number): Promise<void> {
  await authedWrite('POST', `/api/v1/communications/${id}/read`, {});
}

/** OWNER/DIRECTOR only server-side (communicationMustReadGate,
 * src/intake_routes.js) — the chip that calls this is hidden from a
 * manager already, this is the defense that actually matters (INV-3). */
export async function setCommunicationMustRead(id: number, mustRead: boolean): Promise<void> {
  await authedWrite('POST', `/api/v1/communications/${id}/must-read`, { mustRead });
}

/** OWNER only server-side (src/intake_routes.js setCommunicationDeskRetired).
 * Takes the item off everyone's My Desk; it stays in Communications. */
export async function setCommunicationDeskRetired(id: number, retired: boolean): Promise<void> {
  await authedWrite('POST', `/api/v1/communications/${id}/${retired ? 'retire' : 'unretire'}`, {});
  await refetchResource(COMMUNICATIONS_KEY, fetchCommunications);
}

// --- What's New (owner, 2026-10-06; src/whats_new.js, 095/096) ------------

export interface WhatsNewRow {
  id: number;
  subject: string | null;
  bodyText: string | null;
  publishedAt: string | null;
  publishedBy: string | null;
}

/** A new draft — the owner's eyes only until he publishes it. */
export function createWhatsNewDraft(subject: string, bodyText: string): Promise<{ whatsNew: WhatsNewRow }> {
  return authedWrite('POST', '/api/v1/whats-new', { subject, bodyText });
}

export function updateWhatsNewDraft(id: number, subject: string, bodyText: string): Promise<{ whatsNew: WhatsNewRow }> {
  return authedWrite('PUT', `/api/v1/whats-new/${id}`, { subject, bodyText });
}

/** Out to everyone's Communications and My Desk. */
export function publishWhatsNew(id: number): Promise<{ whatsNew: WhatsNewRow }> {
  return authedWrite('POST', `/api/v1/whats-new/${id}/publish`, {});
}
